'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { UIEvent, useMemo, useState } from 'react';

import { ChevronRightIcon, Loader } from '@/shared';
import { useUpdate } from '@/shared/hooks';
import { throttle } from '@/shared/utils';

import { Button } from '../../';
import type { ITrainingTakes } from '../../types';

export const TrainingTakes = ({
	items,
	buttonText,
	buttonHref,
}: ITrainingTakes) => {
	const [active, setActive] = useState(0);
	const [loading, setLoading] = useState(true);
	const item = useMemo(() => items[active], [items, active]);

	const [onScroll] = throttle((event: UIEvent<HTMLElement>) => {
		const containerRect = event.currentTarget.getBoundingClientRect();
		const containerCenterX = containerRect.left + containerRect.width / 2;

		const children = Array.from(event.currentTarget.children);

		let finedKey = active;
		let minDistance = Infinity;

		children.forEach((child, key) => {
			const childRect = child.getBoundingClientRect();
			const childCenterX = childRect.left + childRect.width / 2;

			const distance = Math.abs(containerCenterX - childCenterX);

			if (distance < minDistance) {
				minDistance = distance;
				finedKey = key;
			}
		});

		setActive(finedKey);
	}, 50);

	useUpdate(() => {
		if (item.video) {
			setLoading(true);
		}
	}, [item]);

	return (
		<div className="relative flex flex-col gap-4 xl:grid xl:grid-cols-2 2xl:gap-10">
			{item.video && (
				<div className="relative mx-auto aspect-[351/335] w-full rounded-[40px] bg-white xl:aspect-[896/873] xl:min-h-full">
					{loading && <Loader size="lg" type="fullscreen" />}
					<iframe
						className="absolute left-1/2 top-1/2 aspect-[740/415] h-auto w-full -translate-x-1/2 -translate-y-1/2 object-contain"
						src={item.video}
						allow="autoplay; loop; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write;"
						onLoad={() => setLoading(false)}
					/>
				</div>
			)}
			{item.image && (
				<Image
					src={item.image}
					alt={item.title}
					objectFit="contain"
					loading="lazy"
					className="mx-auto aspect-[351/335] rounded-[40px] bg-white object-contain xl:aspect-[896/873] xl:min-h-full"
					width={896}
					height={873}
				/>
			)}
			<div
				className="-m-3 flex snap-x snap-mandatory flex-nowrap gap-3 p-3 max-xl:overflow-x-auto xl:m-0 xl:flex-col xl:p-0 2xl:gap-6"
				onScroll={onScroll}
			>
				{items.map((item, key) => {
					const className = clsx(
						'flex w-10/12 shrink-0 cursor-pointer snap-center flex-col gap-4 rounded-2xl border border-[#DBDFE9] bg-white p-4 max-xl:max-w-md xl:w-auto xl:gap-6 xl:rounded-3xl xl:p-6 2xl:rounded-[40px] 2xl:p-8',
						{
							'xl:shadow-listbox': key === active,
						},
					);
					const titleClassName = clsx(
						'text-base/[1.2] font-semibold xl:text-2xl/[1.3]',
						{
							'text-[#090E1C]': key === active,
							'text-[#4B5675]': key !== active,
						},
					);
					const textClassName = clsx('text-sm/[1.4] xl:text-base/[1.4]', {
						'text-[#4B5675]': key === active,
						'text-[#99A1B7]': key !== active,
					});
					return (
						<div key={key} className={className} onClick={() => setActive(key)}>
							<div className="space-y-2">
								{item.video && (
									<div>
										<span className="rounded bg-[#41B522] px-2 py-0.5 text-white">
											Видео
										</span>
									</div>
								)}
								<h3 className={titleClassName}>{item.title}</h3>
								<p className={textClassName}>{item.text}</p>
							</div>
						</div>
					);
				})}
			</div>
			<div className="xl:absolute xl:bottom-11 xl:left-10">
				<Button
					href={buttonHref}
					variant="primary"
					size="72"
					className="w-full xl:w-auto"
				>
					{buttonText}
					<ChevronRightIcon className="size-5" />
				</Button>
			</div>
		</div>
	);
};
